import React, { forwardRef, useEffect, useRef, useState } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import { IMaskInput } from "react-imask";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { ptBR } from "date-fns/locale/pt-BR";
import "react-datepicker/dist/react-datepicker.css";

registerLocale("pt-BR", ptBR);

const monthOptions = [
  "janeiro",
  "fevereiro",
  "março",
  "abril",
  "maio",
  "junho",
  "julho",
  "agosto",
  "setembro",
  "outubro",
  "novembro",
  "dezembro",
];

const weekDayLabels = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];

const isoToDate = (value) => {
  if (!value) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return new Date(`${value}T00:00:00`);
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
    const [day, month, year] = value.split("/");
    return new Date(`${year}-${month}-${day}T00:00:00`);
  }
  return null;
};

const dateToIso = (date) => {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDisplay = (value) => {
  if (!value) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split("-");
    return `${day}/${month}/${year}`;
  }
  return value;
};

const parseDisplay = (value) => {
  if (!value || value.length !== 10) return "";
  const [day, month, year] = value.split("/");
  if (!day || !month || !year) return "";
  const parsed = new Date(`${year}-${month}-${day}T00:00:00`);

  if (
    Number.isNaN(parsed.getTime()) ||
    parsed.getFullYear() !== Number(year) ||
    parsed.getMonth() + 1 !== Number(month) ||
    parsed.getDate() !== Number(day)
  ) {
    return "";
  }

  return `${year}-${month}-${day}`;
};

const CompactDateSelect = ({ value, options, onChange, ariaLabel, className = "" }) => {
  const [open, setOpen] = useState(false);
  const selectRef = useRef(null);
  const selectedOptionRef = useRef(null);
  const selectedOption = options.find((option) => option.value === value);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!open) return;

    window.requestAnimationFrame(() => {
      selectedOptionRef.current?.scrollIntoView({
        block: "center",
        inline: "nearest",
      });
    });
  }, [open, value]);

  const handleSelect = (nextValue) => {
    onChange(nextValue);
    setOpen(false);
  };

  return (
    <div className={`fz-date-compact-select ${className}`.trim()} ref={selectRef}>
      <button
        type="button"
        className={`fz-date-compact-select__trigger${open ? " is-open" : ""}`}
        aria-label={ariaLabel}
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <span>{selectedOption?.label || value}</span>
        <ChevronRight size={14} />
      </button>

      {open && (
        <div className="fz-date-compact-select__menu" role="listbox">
          {options.map((option) => (
            <button
              key={option.value}
              ref={option.value === value ? selectedOptionRef : undefined}
              type="button"
              className={`fz-date-compact-select__option${option.value === value ? " is-selected" : ""}`}
              onClick={() => handleSelect(option.value)}
              role="option"
              aria-selected={option.value === value}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const DatePickerHeader = ({
  date,
  changeMonth,
  changeYear,
  decreaseMonth,
  increaseMonth,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
  yearOptions,
}) => (
  <div className="fz-date-header-panel">
    <div className="fz-date-header">
      <button
        type="button"
        className="fz-date-nav"
        onClick={decreaseMonth}
        disabled={prevMonthButtonDisabled}
        aria-label="Mês anterior"
      >
        <ChevronLeft size={18} />
      </button>

      <div className="fz-date-header-selects">
        <CompactDateSelect
          value={date.getMonth()}
          onChange={(nextValue) => changeMonth(Number(nextValue))}
          options={monthOptions.map((month, index) => ({ value: index, label: month }))}
          ariaLabel="Selecionar mês"
          className="fz-date-compact-select--month"
        />

        <CompactDateSelect
          value={date.getFullYear()}
          onChange={(nextValue) => changeYear(Number(nextValue))}
          options={yearOptions.map((year) => ({ value: year, label: String(year) }))}
          ariaLabel="Selecionar ano"
          className="fz-date-compact-select--year"
        />
      </div>

      <button
        type="button"
        className="fz-date-nav"
        onClick={increaseMonth}
        disabled={nextMonthButtonDisabled}
        aria-label="Próximo mês"
      >
        <ChevronRight size={18} />
      </button>
    </div>

    <div className="fz-date-weekdays" aria-hidden="true">
      {weekDayLabels.map((label) => (
        <span key={label}>{label}</span>
      ))}
    </div>
  </div>
);

const DateInput = forwardRef(
  ({ displayValue, onClick, onInputChange, onBlur, placeholder, disabled, name, hasError }, ref) => {
    const [draftValue, setDraftValue] = useState(displayValue || "");

    useEffect(() => {
      setDraftValue(displayValue || "");
    }, [displayValue]);

    const handleAccept = (nextValue = "") => {
      setDraftValue(nextValue);

      if (!nextValue) {
        onInputChange?.("");
        return;
      }

      if (nextValue.length === 10) {
        const parsedValue = parseDisplay(nextValue);
        if (parsedValue) onInputChange?.(parsedValue);
      }
    };

    return (
      <div
        className={`fz-date-input${hasError ? " has-error" : ""}${disabled ? " is-disabled" : ""}`}
        onClick={disabled ? undefined : onClick}
      >
        <CalendarDays size={17} />
        <IMaskInput
          mask="00/00/0000"
          value={draftValue}
          inputRef={ref}
          name={name}
          disabled={disabled}
          placeholder={placeholder}
          unmask={false}
          onAccept={handleAccept}
          onBlur={onBlur}
        />
      </div>
    );
  }
);

DateInput.displayName = "PainelDatePickerInput";

const PainelDatePicker = ({
  value,
  onChange,
  name,
  placeholder = "dd/mm/aaaa",
  disabled = false,
  minDate,
  maxDate,
  error,
  onBlur,
  className = "",
  popperPlacement = "bottom-start",
  onCalendarOpen,
  onCalendarClose,
}) => {
  const selected = isoToDate(value);
  const displayValue = formatDisplay(value);
  const minDateValue = minDate ? isoToDate(minDate) : undefined;
  const maxDateValue = maxDate ? isoToDate(maxDate) : undefined;

  const yearOptions = (() => {
    const currentYear = new Date().getFullYear();
    const selectedYear = selected?.getFullYear();
    const start = Math.min(minDateValue?.getFullYear() ?? currentYear - 10, selectedYear ?? currentYear);
    const end = Math.max(maxDateValue?.getFullYear() ?? currentYear + 10, selectedYear ?? currentYear);

    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  })();

  const emitChange = (nextValue) => {
    if (typeof onChange === "function") {
      onChange({ target: { name, value: nextValue } });
    }
  };

  return (
    <div className={`fz-date-picker ${className}`.trim()}>
      <DatePicker
        selected={selected}
        onChange={(date) => emitChange(dateToIso(date))}
        locale="pt-BR"
        dateFormat="dd/MM/yyyy"
        formatWeekDay={(dayName) => dayName.slice(0, 3)}
        placeholderText={placeholder}
        disabled={disabled}
        minDate={minDateValue}
        maxDate={maxDateValue}
        popperPlacement={popperPlacement}
        popperClassName="fz-date-popper"
        calendarClassName="fz-date-calendar"
        onCalendarOpen={onCalendarOpen}
        onCalendarClose={onCalendarClose}
        renderCustomHeader={(headerProps) => (
          <DatePickerHeader {...headerProps} yearOptions={yearOptions} />
        )}
        customInput={
          <DateInput
            displayValue={displayValue}
            name={name}
            placeholder={placeholder}
            disabled={disabled}
            hasError={Boolean(error)}
            onInputChange={emitChange}
            onBlur={onBlur}
          />
        }
      />
      {error && <div className="error-text">{error}</div>}
    </div>
  );
};

export default PainelDatePicker;
